import React, { useContext } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  Area,
  AreaChart,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  AccountingContext,
  ChartTypeInterface,
} from "../../../providers/AccountingProvider";
import {
  capitalizeWords,
  formatMonthYear,
  formatUniversalNumber,
  getYearFromDate,
} from "../../../lib/utils";
import { ChartSkeleton } from "../../../components/Skeleton/BarChartSkeleton";
import { calculateNetProfit } from "../Summaries";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

function FinancialChart() {
  const {
    chartType,
    setChartType,
    currentDate,
    netProfitData,
    isChartLoading,
  } = useContext(AccountingContext);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Calculate total profit (revenue - expense)
      const revenue =
        payload.find((entry: any) => entry.name === "revenue")?.value || 0;
      const expense =
        payload.find((entry: any) => entry.name === "expense")?.value || 0;
      const totalProfit = calculateNetProfit(revenue, expense);

      return (
        <div className="BookEZ_card_bg_secondary backdrop-blur-sm p-4 rounded-lg shadow-lg border BookEZ_card_border_color">
          {/* <p className="BookEZ_font_bold BookEZ_font_body_large mb-2 BookEZ_font_header_color">
            {label}
          </p> */}
          <div className="space-y-2">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex items-center gap-3 py-1">
                <div
                  className={`w-3 h-3 rounded-full`}
                  style={{ backgroundColor: entry.color }}
                />
                <span className="BookEZ_font_color_gray_2 min-w-[80px]">
                  {capitalizeWords(entry.name)}
                </span>
                <span className="BookEZ_font_body_medium BookEZ_font_color_gray_3">
                  {formatUniversalNumber(entry.value)}
                </span>
              </div>
            ))}
            <hr className="BookEZ_font_color_gray_4" />
            <div className="flex items-center gap-3 py-1">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: "#3b82f6" }}
              />
              <span className="BookEZ_font_color_gray_2 min-w-[80px]">
                Net Profit
              </span>
              <span className="BookEZ_font_body_medium BookEZ_font_color_gray_3">
                {totalProfit}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Add these gradient definitions inside the return statement, in the defs section
  return (
    <Card
      className={`h-full w-full BookEZ_card_bg border BookEZ_card_border_color BookEZ_card_radius shadow-lg hover:shadow-lg transition-shadow duration-300`}
    >
      <CardHeader className="py-4">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <CardTitle className="BookEZ_font_body_large BookEZ_font_bold! BookEZ_font_color_gray_3">
            Net Profit
          </CardTitle>
          <div className="flex flex-wrap gap-4 pt-1 justify-start items-center">
            {/* <div className="BookEZ_font_body_size BookEZ_font_color_gray_2 BookEZ_font_light_bold">
              {getYearFromDate(currentDate)}
            </div> */}
            {/* <Tabs
              value={chartType}
              defaultValue={chartType}
              onValueChange={(value: string) =>
                setChartType(value as ChartTypeInterface)
              }
            >
              <TabsList className="grid w-48 grid-cols-2 BookEZ_font_color_gray_2">
                <TabsTrigger value="bar">Bar</TabsTrigger>
                <TabsTrigger value="line">Line</TabsTrigger>
              </TabsList>
            </Tabs> */}
            <Select
              value={chartType === "line" ? "Line Chart" : "Bar Chart"}
              onValueChange={(value: string) => {
                if (value == "Line Chart") {
                  setChartType("line");
                } else if (value == "Bar Chart") {
                  setChartType("bar");
                }
              }}
            >
              <SelectTrigger className="h-8 w-[120px] !rounded-md BookEZ_card_border_color">
                <SelectValue placeholder={"Line Chart"} />
              </SelectTrigger>
              <SelectContent side="bottom">
                {["Line Chart", "Bar Chart"].map((label) => (
                  <SelectItem key={label} value={label}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="BookEZ_font_body_size BookEZ_font_color_gray_2 BookEZ_font_light_bold">
              {getYearFromDate(currentDate)}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="h-[400px] w-full p-4 transition-all duration-300">
          {isChartLoading ? (
            <ChartSkeleton />
          ) : (
            <>
              <ResponsiveContainer width="100%" height="100%">
                {chartType === "line" ? (
                  <AreaChart
                    data={netProfitData}
                    onMouseMove={(e: any) => {
                      if (e.activeTooltipIndex !== undefined) {
                      }
                    }}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="revenueGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#22c55e"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#22c55e"
                          stopOpacity={0.05}
                        />
                      </linearGradient>
                      <linearGradient
                        id="profitGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#3b82f6"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#3b82f6"
                          stopOpacity={0.05}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted/50"
                    />
                    <XAxis
                      dataKey="month"
                      className="BookEZ_font_body_size"
                      tick={{ fill: "#6b7280" }}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <YAxis
                      className="BookEZ_font_body_size"
                      tick={{ fill: "#6b7280" }}
                      tickFormatter={(value) => `$${value}k`}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <RechartsTooltip
                      content={<CustomTooltip />}
                      cursor={{
                        stroke: "#6b7280",
                        strokeWidth: 1,
                        strokeDasharray: "4 4",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#22c55e"
                      strokeWidth={3}
                      fill="url(#revenueGradient)"
                      dot={{
                        r: 4,
                        fill: "#22c55e",
                        strokeWidth: 2,
                        stroke: "#fff",
                      }}
                      activeDot={{
                        r: 8,
                        fill: "#22c55e",
                        stroke: "#fff",
                        strokeWidth: 3,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="expense"
                      stroke="#ef4444"
                      strokeWidth={3}
                      fill="none"
                      dot={{
                        r: 4,
                        fill: "#ef4444",
                        strokeWidth: 2,
                        stroke: "#fff",
                      }}
                      activeDot={{
                        r: 8,
                        fill: "#ef4444",
                        stroke: "#fff",
                        strokeWidth: 3,
                      }}
                    />
                  </AreaChart>
                ) : (
                  <BarChart
                    data={netProfitData}
                    onMouseMove={(e: any) => {
                      if (e.activeTooltipIndex !== undefined) {
                      }
                    }}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="revenueBarGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#22c55e"
                          stopOpacity={0.6}
                        />
                      </linearGradient>
                      <linearGradient
                        id="expenseBarGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#ef4444"
                          stopOpacity={0.6}
                        />
                      </linearGradient>
                      <linearGradient
                        id="profitBarGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#3b82f6"
                          stopOpacity={0.6}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted/30"
                    />
                    <XAxis
                      dataKey="month"
                      className="BookEZ_font_body_size"
                      tick={{ fill: "#6b7280" }}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <YAxis
                      className="BookEZ_font_body_size"
                      tick={{ fill: "#6b7280" }}
                      tickFormatter={(value) => `$${value}k`}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <RechartsTooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                    />
                    <Bar
                      dataKey="revenue"
                      // stackId="a"
                      fill="#25935e"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={20}
                      fillOpacity={0.9}
                      onMouseOver={() => {
                        document.body.style.cursor = "pointer";
                      }}
                      onMouseOut={() => {
                        document.body.style.cursor = "default";
                      }}
                    />
                    <Bar
                      // stackId="a"
                      dataKey="expense"
                      fill="#d73b38"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={20}
                      fillOpacity={0.9}
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default FinancialChart;
